import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { mortgageFieldNames } from 'app/pages/loanApplication/LoanApplication.sharedConfigs';
import {
  checkIsAmountOfRentCollectedActive,
  checkIsSoldPropertyStatus,
  getExpenses,
  getFormFieldsValuesByGroupName,
} from 'app/pages/loanApplication/LoanApplication.utils';
import dayjs from 'dayjs';
import _ from 'lodash';

import {
  OccupancyType,
  PropertyMortgageLiabilityType,
  PropertyMortgageType,
} from 'constants/loanApplication';
import { getConditionalObject, normalizeRequestPhoneNumber } from 'utils/data';
import { jsonParse, jsonStringify } from 'utils/json';
import {
  checkBooleanPendingChangeVisibility,
  getApplicableAddressFieldsPendingChanges,
  mergePendingChanges,
} from 'utils/pendingChanges';

import { ALL_BORROWERS_PATH, NEW_RECORD_DATA } from '../../../../FinancialInfo.constants';
import { mapFieldsToFalse } from '../SectionContent.utils';

import { PROPERTY_MODEL_TYPE } from './Reo.constants';

const booleanFields = [
  'firstMortgagetoBePaidOffAtOrBeforeClosing',
  'firstMortgageexcludePayment',
  'firstMortgageisSubordinate',
  'secondMortgagetoBePaidOffAtOrBeforeClosing',
  'secondMortgageexcludePayment',
  'secondMortgageisSubordinate',
];

const defaultBooleanValues = mapFieldsToFalse(booleanFields);

export const normalizeReceivedReoData = data => {
  return Object.entries(data).reduce(
    (acc, [id, reoData]) => {
      const {
        address,
        propertyType,
        mortgages,
        attachmentType,
        occupancy,
        expenses,
        value,
        rentalIncomeDetails,
        liabilityOptions,
        status,
        ...restReoData
      } = reoData || {};

      const formattedAddress = {
        ...address?.address,
        ...address?.address?.country,
        state: address?.address?.country?.state?.abbreviation,
        country: address?.address?.country?.type,
      };

      const { otherExpenses, associationFee, ...restExpenses } = expenses;

      const formattedOtherExpenses = (otherExpenses || []).map((item, index) => ({
        ...item,
        value: item?.amount,
        index,
      }));

      const firstMortgageId = mortgages?.firstMortgageId;
      const secondMortgageId = mortgages?.secondMortgageId;

      const firstMortgage = liabilityOptions.find(({ value }) => value === firstMortgageId);
      const secondMortgage = liabilityOptions.find(({ value }) => value === secondMortgageId);

      const { firstMortgageFields, secondMortgageFields } = mortgageFieldNames.reduce(
        (acc, fieldName) => {
          const firstMortgageFieldName = `${PropertyMortgageType.FIRST}${fieldName}`;
          const secondMortgageFieldName = `${PropertyMortgageType.SECOND}${fieldName}`;

          _.set(
            acc,
            `firstMortgageFields[${firstMortgageFieldName}]`,
            firstMortgage?.[fieldName] ?? defaultBooleanValues[firstMortgageFieldName] ?? null,
          );

          _.set(
            acc,
            `secondMortgageFields[${secondMortgageFieldName}]`,
            secondMortgage?.[fieldName] ?? defaultBooleanValues[secondMortgageFieldName] ?? null,
          );

          return acc;
        },
        { firstMortgageFields: {}, secondMortgageFields: {} },
      );

      const formattedReoData = {
        ...restReoData,
        ...restExpenses,
        ...associationFee,
        ...mortgages,
        ...rentalIncomeDetails,
        status,
        rentCalculatedBasedOn: rentalIncomeDetails?.calculationType,
        address: formattedAddress,
        hasOtherExpenses: !!otherExpenses?.length,
        expenses: getExpenses(expenses),
        otherExpenses: formattedOtherExpenses,
        propertyType: propertyType?.type,
        propertyValue: value,
        occupancy: occupancy?.type,
        attachmentType: attachmentType?.type,
        hasFirstMortgage: !!firstMortgageId,
        hasSecondMortgage: !!secondMortgageId,
        taxesEscrowed: expenses?.taxesEscrowed ?? expenses?.taxesAndInsuranceAreEscrowed,
        homeownersInsuranceEscrowed:
          expenses?.homeownersInsuranceEscrowed ?? expenses?.taxesAndInsuranceAreEscrowed,
        associationFees: !!associationFee,
        associationFeesAmount: associationFee?.monthlyFee,
        associationFeesName: associationFee?.associationName,
        associationFeesEmail: associationFee?.associationEmail,
        associationFeesPhoneNumber: associationFee?.associationPhoneNumber,
        monthlyFee: associationFee?.monthlyFee,
        ...defaultBooleanValues,
        ...getConditionalObject(firstMortgage, () => ({
          [PropertyMortgageLiabilityType.FIRST]: firstMortgage?.id,
          ...firstMortgageFields,

          ...getConditionalObject(secondMortgage, {
            [PropertyMortgageLiabilityType.SECOND]: secondMortgage?.id,
            ...secondMortgageFields,
          }),
        })),
      };

      acc.formattedData[id] = formattedReoData;
      acc.rawData[id] = formattedReoData;

      return acc;
    },
    {
      formattedData: {},
      rawData: {},
    },
  );
};

export const normalizeReoDataToSend = ({ deletedRecords, dataToNormalize, rawData }) => {
  const { normalizedDataToUpdate, normalizedDataToCreate } = Object.entries(dataToNormalize).reduce(
    (acc, [recordId, recordData]) => {
      const recordRawData = rawData[recordId] || {};
      const isSubjectProperty = recordRawData?.type === PROPERTY_MODEL_TYPE.SUBJECT_PROPERTY;

      if (deletedRecords.has(recordId) || isSubjectProperty) {
        return acc;
      }

      const { owners, address, occupancy, status, propertyType, ...restRecordData } = recordData;

      const firstMortgageFields = getFormFieldsValuesByGroupName(
        recordData,
        PropertyMortgageType.FIRST,
      );

      const secondMortgageFields = getFormFieldsValuesByGroupName(
        recordData,
        PropertyMortgageType.SECOND,
      );

      const isAmountOfRentCollectedActive = checkIsAmountOfRentCollectedActive({
        occupancy: recordData?.occupancy?.value,
        propertyType: recordData?.propertyType?.value,
      });

      const { country, ...restAddress } = address;

      const formattedAddress = {
        ...restAddress,
        country: country?.value,
        state: address?.state?.value,
        county: address?.county?.value,
      };

      const ownersToSend = owners ? owners.map(({ value }) => value) : recordRawData?.owners;

      const normalizedRecordData = {
        ...restRecordData,
        occupancy: occupancy?.value,
        status: status?.value,
        propertyType: propertyType?.value,
        address: formattedAddress,
        propertyId: restRecordData?.propertyId,
        yearBuilt: dayjs(recordData?.yearBuilt).format('YYYY'),
        associationFeesPhoneNumber: normalizeRequestPhoneNumber(
          recordData?.associationFeesPhoneNumber,
        ),
        firstMortgageId: firstMortgageFields?.liabilityId?.value,
        secondMortgageId: secondMortgageFields?.liabilityId?.value,
        firstMortgage: recordData?.hasFirstMortgage
          ? {
              ...firstMortgageFields,
              liabilityId: firstMortgageFields.liabilityId?.value,
              toBePaidOffAtOrBeforeClosing: !!firstMortgageFields.toBePaidOffAtOrBeforeClosing,
              isSubordinate: !!firstMortgageFields.isSubordinate,
              excludePayment: !!firstMortgageFields.excludePayment,
            }
          : null,
        secondMortgage: recordData?.hasSecondMortgage
          ? {
              ...secondMortgageFields,
              liabilityId: secondMortgageFields.liabilityId?.value,
              toBePaidOffAtOrBeforeClosing: !!secondMortgageFields.toBePaidOffAtOrBeforeClosing,
              isSubordinate: !!secondMortgageFields.isSubordinate,
              excludePayment: !!secondMortgageFields.excludePayment,
            }
          : null,
        owners: ownersToSend,
        otherExpenses: recordData?.hasOtherExpenses ? recordData?.otherExpenses : [],
        ...(isAmountOfRentCollectedActive
          ? {}
          : {
              rentCalculatedBasedOn: null,
              monthlyRentalIncome: null,
              expenses: null,
              netMonthlyRentalIncome: null,
            }),
        requestMetadata: { tabId: LoanApplicationTabId.REO },
      };

      if (recordId.startsWith(NEW_RECORD_DATA.id)) {
        acc.normalizedDataToCreate.push(normalizedRecordData);
      }

      if (!recordId.startsWith(NEW_RECORD_DATA.id)) {
        acc.normalizedDataToUpdate[recordId] = normalizedRecordData;
      }

      return acc;
    },
    { normalizedDataToUpdate: {}, normalizedDataToCreate: [] },
  );

  return {
    create: normalizedDataToCreate,
    update: normalizedDataToUpdate,
    delete: Array.from(deletedRecords),
  };
};

export const normalizeForm = ({ form, recordId, borrowerId, ownersOptions }) => {
  const {
    owners,
    status,
    hasFirstMortgage,
    firstMortgageliabilityId,
    firstMortgageunpaidBalance,
    firstMortgagemonthlyPayment,
    firstMortgagetoBePaidOffAtOrBeforeClosing,
    firstMortgageexcludePayment,
    firstMortgageisSubordinate,

    hasSecondMortgage,
    secondMortgageliabilityId,
    secondMortgageunpaidBalance,
    secondMortgagemonthlyPayment,
    secondMortgagetoBePaidOffAtOrBeforeClosing,
    secondMortgageexcludePayment,
    secondMortgageisSubordinate,
    ...restRecordData
  } = form[recordId] ?? {};

  const initialOwners =
    recordId.startsWith(NEW_RECORD_DATA.id) &&
    ownersOptions.length &&
    borrowerId !== ALL_BORROWERS_PATH
      ? [ownersOptions.find(({ value }) => value === borrowerId)]
      : owners;

  const formattedHasSecondMortgage = hasFirstMortgage && hasSecondMortgage;

  const isSoldPropertyStatus = checkIsSoldPropertyStatus(status?.value);

  return {
    ...form,
    [recordId]: {
      ...restRecordData,
      status,

      hasFirstMortgage,
      ...getConditionalObject(hasFirstMortgage, {
        firstMortgageliabilityId,
        firstMortgageunpaidBalance,
        firstMortgagemonthlyPayment,
        firstMortgagetoBePaidOffAtOrBeforeClosing,
        firstMortgageexcludePayment: isSoldPropertyStatus || firstMortgageexcludePayment,
        firstMortgageisSubordinate,
      }),

      hasSecondMortgage: formattedHasSecondMortgage,
      ...getConditionalObject(formattedHasSecondMortgage, {
        secondMortgageliabilityId,
        secondMortgageunpaidBalance,
        secondMortgagemonthlyPayment,
        secondMortgagetoBePaidOffAtOrBeforeClosing,
        secondMortgageexcludePayment: isSoldPropertyStatus || secondMortgageexcludePayment,
        secondMortgageisSubordinate,
      }),

      owners: owners ?? initialOwners,
    },
  };
};

const parseOtherExpensesPendingChange = ({ newValue, initialValue } = {}) => {
  const parsedNewValue = newValue && jsonParse(newValue);
  const parsedInitialValue = initialValue && jsonParse(initialValue);

  return parsedNewValue?.reduce((acc, expense, index) => {
    const formattedExpense = {
      name: {
        newValue: expense.Name,
        initialValue: parsedInitialValue?.[index]?.Name,
      },
      value: {
        newValue: expense.Value,
        initialValue: parsedInitialValue?.[index]?.Value,
      },
    };

    acc.push(formattedExpense);

    return acc;
  }, []);
};

const excludedFromMortgageFields = [
  'accountNumber',
  'accountType',
  'companyName',
  'dateOpened',
  'heloc',
  'loanType',
  'numberRemainingPayments',
  'qualifiedNonCashOut',
  'monthsRemaining',
];

const filterMortgageFields = (mortgage = {}) => _.omit(mortgage, excludedFromMortgageFields);

const extractMortgagePendingChanges = (mortgageName, mortgagePendingChanges) =>
  _.mapKeys(mortgagePendingChanges, (_, key) => `${mortgageName}${key}`);

const normalizeInitialPendingChanges = pendingChanges => {
  return Object.entries(pendingChanges).reduce((acc, [recordId, recordPendingChanges]) => {
    const {
      otherExpenses,
      firstMortgageId,
      secondMortgageId,
      firstMortgage,
      secondMortgage,
      ...resetPendingChanges
    } = recordPendingChanges;

    acc[recordId] = {
      ...getConditionalObject(otherExpenses, () => ({
        otherExpenses: parseOtherExpensesPendingChange(otherExpenses),
      })),
      ...getConditionalObject(firstMortgageId, {
        firstMortgageliabilityId: firstMortgageId,
        hasFirstMortgage: {
          newValue: !!firstMortgageId?.newValue,
          initialValue: !!firstMortgageId?.initialValue,
        },
      }),
      ...getConditionalObject(secondMortgageId, {
        secondMortgageliabilityId: secondMortgageId,
        hasSecondMortgage: {
          newValue: !!secondMortgageId?.newValue,
          initialValue: !!secondMortgageId?.initialValue,
        },
      }),
      ...getConditionalObject(firstMortgage, () => ({
        ...extractMortgagePendingChanges('firstMortgage', filterMortgageFields(firstMortgage)),
      })),
      ...getConditionalObject(secondMortgage, () => ({
        ...extractMortgagePendingChanges('secondMortgage', filterMortgageFields(secondMortgage)),
      })),
      ...resetPendingChanges,
    };

    return acc;
  }, {});
};

const filterPendingChanges = pendingChanges =>
  Object.entries(pendingChanges).reduce((acc, [recordId, recordPendingChanges]) => {
    const filteredPendingChanges = Object.entries(recordPendingChanges).reduce(
      (filteredPendingChangesAcc, [pendingChangeKey, pendingChangeValue]) => {
        const occupancyNewValue = recordPendingChanges.occupancy?.newValue;
        const associationFeesNewValue = recordPendingChanges.associationFees?.newValue;
        const hasOtherExpensesNewValue = recordPendingChanges.hasOtherExpenses?.newValue;
        const isFirstMortgageOnThePropertyNewValue =
          recordPendingChanges.hasFirstMortgage?.newValue;
        const isSecondMortgageOnThePropertyNewValue =
          recordPendingChanges.hasSecondMortgage?.newValue;

        const isOccupancyChanged = occupancyNewValue !== undefined;
        const isAssociationFeesChanged = associationFeesNewValue !== undefined;
        const isHasOtherExpensesChanged = hasOtherExpensesNewValue !== undefined;
        const isFirstMortgageOnThePropertyChanged =
          isFirstMortgageOnThePropertyNewValue !== undefined;
        const isSecondMortgageOnThePropertyChanged =
          isSecondMortgageOnThePropertyNewValue !== undefined;

        const getCondition = () => {
          switch (pendingChangeKey) {
            case 'mortgagesPayment':
            case 'annualPropertyTaxes':
            case 'annualHomeownersInsurance':
            case 'expenses':
            case 'netMonthlyRentalIncome':
              return false;

            case 'associationFeesAmount':
            case 'associationFeesName':
            case 'associationFeesEmail':
            case 'associationFeesPhoneNumber':
              return !isAssociationFeesChanged || associationFeesNewValue;

            case 'otherExpenses':
              return !isHasOtherExpensesChanged || hasOtherExpensesNewValue;

            case 'firstMortgageId':
              return !!pendingChangeValue.newValue;

            case 'secondMortgageId':
              return !!pendingChangeValue.newValue;

            case 'firstMortgageliabilityId':
            case 'firstMortgageunpaidBalance':
            case 'firstMortgagemonthlyPayment':
              return !isFirstMortgageOnThePropertyChanged || !!isFirstMortgageOnThePropertyNewValue;

            case 'firstMortgagetoBePaidOffAtOrBeforeClosing':
            case 'firstMortgageexcludePayment':
            case 'firstMortgageisSubordinate':
              return (
                !isFirstMortgageOnThePropertyChanged ||
                !!(
                  isFirstMortgageOnThePropertyNewValue &&
                  checkBooleanPendingChangeVisibility(pendingChangeValue)
                )
              );

            case 'secondMortgageliabilityId':
            case 'secondMortgageunpaidBalance':
            case 'secondMortgagemonthlyPayment':
              return (
                (!isFirstMortgageOnThePropertyChanged || !!isFirstMortgageOnThePropertyNewValue) &&
                (!isSecondMortgageOnThePropertyChanged || !!isSecondMortgageOnThePropertyNewValue)
              );

            case 'secondMortgagetoBePaidOffAtOrBeforeClosing':
            case 'secondMortgageexcludePayment':
            case 'secondMortgageisSubordinate':
              return (
                (!isFirstMortgageOnThePropertyChanged || !!isFirstMortgageOnThePropertyNewValue) &&
                (!isSecondMortgageOnThePropertyChanged ||
                  (!!isSecondMortgageOnThePropertyNewValue &&
                    checkBooleanPendingChangeVisibility(pendingChangeValue)))
              );

            case 'monthlyRentalIncome':
              return (
                (!isOccupancyChanged || occupancyNewValue === OccupancyType.INVESTMENT_PROPERTY) &&
                ![pendingChangeValue.newValue, pendingChangeValue.initialValue].every(
                  val => !Number(val),
                )
              );

            default:
              return true;
          }
        };

        const condition = getCondition();

        if (condition) {
          filteredPendingChangesAcc[pendingChangeKey] = pendingChangeValue;
        }

        return filteredPendingChangesAcc;
      },
      {},
    );

    acc[recordId] = filteredPendingChanges;

    return acc;
  }, {});

const formatPendingChangesToForm = ({ pendingChanges, form }) => {
  return Object.entries(pendingChanges).reduce((acc, [recordId, recordPendingChanges]) => {
    const { address, otherExpenses, ...restRecordPendingChanges } = recordPendingChanges;

    const formattedRecordPendingChanges = {
      address: getApplicableAddressFieldsPendingChanges(address),
      ...getConditionalObject(otherExpenses, () => ({
        otherExpenses: otherExpenses?.slice(0, form[recordId]?.otherExpenses?.length),
      })),
      ...restRecordPendingChanges,
    };

    acc[recordId] = formattedRecordPendingChanges;

    return acc;
  }, {});
};

export const normalizeReoPendingChanges = ({
  initialPendingChanges,
  formPendingChanges = {},
  data,
  form,
  isEditFlow = false,
}) => {
  const normalizedInitialPendingChanges = normalizeInitialPendingChanges(initialPendingChanges);

  const mergedPendingChanges = mergePendingChanges(
    normalizedInitialPendingChanges,
    formPendingChanges,
  );

  const filteredPendingChanges = filterPendingChanges(mergedPendingChanges);

  return isEditFlow
    ? formatPendingChangesToForm({
        pendingChanges: filteredPendingChanges,
        data,
        form,
      })
    : filteredPendingChanges;
};

export const getUsedLiabilities = ({ data, form }) => {
  const values = _.values({ ...data, ...form })
    .map(i => [
      i[PropertyMortgageLiabilityType.FIRST]?.value || i[PropertyMortgageLiabilityType.FIRST],
      i[PropertyMortgageLiabilityType.SECOND]?.value || i[PropertyMortgageLiabilityType.SECOND],
    ])
    .flat()
    .filter(Boolean);

  return jsonStringify(values);
};

export const getFilteredLiabilityOptionsById = ({
  subjectPropertyId,
  liabilityOptions,
  usedLiabilities,
  liabilityId,
}) =>
  _.filter(
    liabilityOptions,
    i =>
      i.value === liabilityId ||
      (!jsonParse(usedLiabilities).includes(i.value) && subjectPropertyId !== i.propertyId),
  );

export const getIsOneOfMonthlyDependentParamsFilled = ({
  monthlyPropertyTaxes,
  monthlyHomeownersInsurance,
  monthlyPmi,
}) => !!(monthlyPropertyTaxes || monthlyHomeownersInsurance || monthlyPmi);
